import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuthStore, useNotificationStore, useSubjectsStore } from '../lib/store';
import type { FocusTask } from '../lib/types';

const REMINDER_CACHE_KEY = 'focusflow.reminder-dispatch-cache.v1';

type ReminderDispatchCache = Record<string, string>;

export interface ReminderSnapshot {
  task: FocusTask;
  reminderAt: Date;
  minutesUntil: number;
  bucket: 'soon' | 'due';
}

const readCache = (): ReminderDispatchCache => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(REMINDER_CACHE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as ReminderDispatchCache;
  } catch {
    return {};
  }
};

const writeCache = (cache: ReminderDispatchCache) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(REMINDER_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // Ignore storage failures.
  }
};

const purgeExpiredCache = (cache: ReminderDispatchCache) => {
  const maxAgeMs = 1000 * 60 * 60 * 24 * 7;
  const now = Date.now();
  Object.entries(cache).forEach(([key, timestamp]) => {
    const parsed = Date.parse(timestamp);
    if (!Number.isFinite(parsed) || now - parsed > maxAgeMs) {
      delete cache[key];
    }
  });
};

const buildReminderKey = (task: FocusTask, bucket: ReminderSnapshot['bucket']) => {
  return `${bucket}:${task.id}:${task.reminder_at || task.due_date}`;
};

const canNotifyBrowser = () => typeof window !== 'undefined' && 'Notification' in window;

export function useTaskReminderNotifications() {
  const userId = useAuthStore((state) => state.user?.id);
  const tasks = useSubjectsStore((state) => state.tasks);
  const fetchAll = useSubjectsStore((state) => state.fetchAll);
  const addNotification = useNotificationStore((state) => state.addNotification);

  const [isOnline, setIsOnline] = useState(() => (typeof navigator !== 'undefined' ? navigator.onLine : true));
  const [browserPermission, setBrowserPermission] = useState<NotificationPermission | 'unsupported'>(() => {
    if (!canNotifyBrowser()) return 'unsupported';
    return window.Notification.permission;
  });
  const [now, setNow] = useState(() => Date.now());
  const dispatchCacheRef = useRef<ReminderDispatchCache>(readCache());

  useEffect(() => {
    if (userId) {
      void fetchAll();
    }
  }, [userId, fetchAll]);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!canNotifyBrowser()) return;
    setBrowserPermission(window.Notification.permission);
  }, []);

  const reminderSnapshots = useMemo<ReminderSnapshot[]>(() => {
    const upcoming = tasks
      .filter((task) => Boolean(task.reminder_at) && !task.is_completed)
      .map((task) => {
        const reminderAt = new Date(task.reminder_at as string);
        const minutesUntil = Math.floor((reminderAt.getTime() - now) / 60000);
        const bucket: ReminderSnapshot['bucket'] = minutesUntil <= 0 ? 'due' : 'soon';
        return { task, reminderAt, minutesUntil, bucket };
      })
      .filter((snapshot) => !Number.isNaN(snapshot.reminderAt.getTime()))
      .sort((a, b) => a.reminderAt.getTime() - b.reminderAt.getTime());

    return upcoming;
  }, [tasks, now]);

  const summary = useMemo(() => {
    const overdue = reminderSnapshots.filter((snapshot) => snapshot.bucket === 'due');
    const soon = reminderSnapshots.filter((snapshot) => snapshot.bucket === 'soon' && snapshot.minutesUntil <= 15);
    const nextReminder = reminderSnapshots[0] || null;
    return {
      overdue,
      soon,
      nextReminder,
      pendingCount: overdue.length + soon.length,
    };
  }, [reminderSnapshots]);

  useEffect(() => {
    if (!userId || reminderSnapshots.length === 0) return;

    const cache = dispatchCacheRef.current;
    purgeExpiredCache(cache);
    let changed = false;

    const dispatchBrowserNotification = (snapshot: ReminderSnapshot, label: string) => {
      if (!canNotifyBrowser() || browserPermission !== 'granted') return;
      if (!document.hidden && snapshot.bucket === 'soon') return;

      const title = snapshot.bucket === 'due' ? 'Task reminder due' : 'Task reminder soon';
      const body = `${snapshot.task.title} is ${label}${snapshot.task.subjects?.name ? ` · ${snapshot.task.subjects.name}` : ''}`;

      try {
        new window.Notification(title, {
          body,
          tag: buildReminderKey(snapshot.task, snapshot.bucket),
          silent: false,
        });
      } catch {
        // Ignore browser notification failures.
      }
    };

    reminderSnapshots.forEach((snapshot) => {
      if (snapshot.bucket === 'soon' && snapshot.minutesUntil > 15) return;

      const key = buildReminderKey(snapshot.task, snapshot.bucket);
      if (cache[key]) return;

      const label =
        snapshot.bucket === 'due'
          ? snapshot.minutesUntil < 0
            ? `${Math.abs(snapshot.minutesUntil)} minute${Math.abs(snapshot.minutesUntil) === 1 ? '' : 's'} overdue`
            : 'due now'
          : `due in ${snapshot.minutesUntil} minute${snapshot.minutesUntil === 1 ? '' : 's'}`;

      addNotification({
        type: snapshot.bucket === 'due' ? 'warning' : 'info',
        title: snapshot.bucket === 'due' ? 'Task reminder due' : 'Task reminder coming up',
        message: `${snapshot.task.title} is ${label}${snapshot.task.subjects?.name ? ` · ${snapshot.task.subjects.name}` : ''}`,
      });
      dispatchBrowserNotification(snapshot, label);
      cache[key] = new Date().toISOString();
      changed = true;
    });

    if (changed) {
      writeCache(cache);
    }
  }, [addNotification, browserPermission, reminderSnapshots, userId]);

  const requestBrowserPermission = async () => {
    if (!canNotifyBrowser()) {
      setBrowserPermission('unsupported');
      return 'unsupported' as const;
    }

    if (window.Notification.permission === 'granted') {
      setBrowserPermission('granted');
      return 'granted' as const;
    }

    if (window.Notification.permission === 'denied') {
      setBrowserPermission('denied');
      return 'denied' as const;
    }

    const nextPermission = await window.Notification.requestPermission();
    setBrowserPermission(nextPermission);
    return nextPermission;
  };

  return {
    browserPermission,
    isOnline,
    pendingCount: summary.pendingCount,
    nextReminder: summary.nextReminder,
    overdueReminders: summary.overdue,
    soonReminders: summary.soon,
    requestBrowserPermission,
    refreshReminders: () => setNow(Date.now()),
  };
}
